/** Memory-only status, never tokens. A failed silent request waits for explicit retry. */
export type GroupAccessState = 'idle' | 'ready' | 'consent' | 'unavailable';
let state: GroupAccessState = 'idle';
const listeners = new Set<() => void>();
let retry: (() => void) | null = null;
export const subscribeGroupAccess = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };
export const getGroupAccessState = () => state;
function publish(value: GroupAccessState) {
  if (state === value) return;
  state = value;
  listeners.forEach(listener => listener());
}
export function retryGroupAccess() { retry?.(); }

/** One controller per signed-in identity, with only an in-flight promise retained. */
export function createGroupAccessController(identity: () => string | null, acquire: () => Promise<string | null>) {
  let failed = false;
  let disposed = false;
  let pending: Promise<string | null> | null = null;
  let epoch = 0;
  const reset = () => { failed = false; epoch++; pending = null; publish('idle'); };
  retry = reset;
  publish('idle');
  return {
    token(): Promise<string | null> {
      if (disposed || failed || !identity()) return Promise.resolve(null);
      if (pending) return pending;
      const original = identity();
      const generation = epoch;
      pending = (async () => {
        try {
          const token = await acquire();
          if (disposed || generation !== epoch || identity() !== original) return null;
          failed = !token;
          publish(token ? 'ready' : 'consent');
          return token;
        } catch (error) {
          if (!disposed && generation === epoch && identity() === original) {
            failed = true;
            const code = error && typeof error === 'object' && 'errorCode' in error ? error.errorCode : '';
            publish(['consent_required', 'interaction_required', 'login_required'].includes(String(code)) ? 'consent' : 'unavailable');
          }
          return null;
        } finally {
          if (generation === epoch) pending = null;
        }
      })();
      return pending;
    },
    dispose() {
      disposed = true;
      epoch++;
      pending = null;
      if (retry === reset) { retry = null; publish('idle'); }
    }
  };
}
